/* eslint-disable */
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Select,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { SearchIcon } from '@chakra-ui/icons';
import { MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card/Card';
import React from 'react';
import { formatCurrency, formatDate, formatStatus } from 'utils/format';

const columnHelper = createColumnHelper();

export default function TuitionsTable(props) {
  const {
    tableData = [],
    pagination,
    searchQuery = '',
    onSearchChange,
    onPageChange,
    onItemsPerPageChange,
  } = props;
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const bgStatus = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const inputBg = useColorModeValue('secondaryGray.300', 'navy.900');
  const inputText = useColorModeValue('secondaryGray.700', 'white');
  const searchIconColor = useColorModeValue('secondaryGray.700', 'white');
  const buttonBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const buttonHover = useColorModeValue(
    { bg: 'secondaryGray.400' },
    { bg: 'whiteAlpha.50' }
  );
  const iconColor = useColorModeValue('brand.500', 'white');

  const columns = [
    columnHelper.accessor('student_name', {
      id: 'student_name',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          STUDENT
        </Text>
      ),
      cell: (info) => (
        <Flex direction="column">
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {info.getValue()}
          </Text>
          <Text color="secondaryGray.600" fontSize="xs">
            {info.row.original.student_matricule}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('student_center', {
      id: 'student_center',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          CENTER
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('type', {
      id: 'type',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          TYPE
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('prepa_session', {
      id: 'prepa_session',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          SESSION
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          AMOUNT
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {formatCurrency(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('total_paid', {
      id: 'total_paid',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          PAID
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {formatCurrency(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('remaining_balance', {
      id: 'remaining_balance',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          BALANCE
        </Text>
      ),
      cell: (info) => {
        const balance = info.getValue();
        const balanceColor = balance < 0 ? 'green.500' : balance > 0 ? 'red.500' : textColor;
        return (
          <Text color={balanceColor} fontSize="sm" fontWeight="700">
            {formatCurrency(balance)}
          </Text>
        );
      },
    }),
    columnHelper.accessor('due_date', {
      id: 'due_date',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          DUE DATE
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {formatDate(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          STATUS
        </Text>
      ),
      cell: (info) => {
        const status = info.getValue();
        const colorScheme = 
          status === 'paid' ? 'green' : 
          status === 'pending' ? 'yellow' : 
          status === 'overdue' ? 'red' : 'gray';
        return (
          <Badge colorScheme={colorScheme} bg={bgStatus} fontSize="xs" fontWeight="700" px="8px" py="4px">
            {formatStatus(status)}
          </Badge>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          ACTIONS
        </Text>
      ),
      cell: (info) => (
        <IconButton
          aria-label="View tuition"
          icon={<MdVisibility />}
          size="sm"
          variant="ghost"
          colorScheme="blue"
          onClick={() => navigate(`/admin/tuition/${info.row.original.id}`)}
        />
      ),
    }),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center" flexWrap="wrap" gap="15px">
        <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
          All Tuitions
        </Text>
        <InputGroup w={{ base: "100%", md: "300px" }}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color={searchIconColor} w="15px" h="15px" />
          </InputLeftElement>
          <Input
            variant="search"
            fontSize="sm"
            bg={inputBg}
            color={inputText}
            fontWeight="500"
            _placeholder={{ color: "secondaryGray.600", fontSize: "14px" }}
            borderRadius="30px"
            placeholder="Search tuitions..."
            value={searchQuery}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          />
        </InputGroup>
      </Flex>
      <Box>
        <Table variant="simple" color="secondaryGray.700" mb="24px" mt="12px">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id} borderColor={borderColor} pe="10px"
                    cursor="pointer"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Flex
                      justifyContent="space-between"
                      align="center"
                      fontSize={{ sm: "10px", lg: "12px" }}
                      color="secondaryGray.600"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" py="20px">
                  <Text color={textColor} fontSize="sm">
                    No tuitions found
                  </Text>
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      fontSize={{ sm: "14px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination Controls */}
      {pagination && (
        <Flex
          px="25px"
          py="20px"
          justifyContent="space-between"
          align="center"
          flexWrap="wrap"
          gap="15px"
          borderTop="1px solid"
          borderColor={borderColor}
        >
          <HStack spacing="10px">
            <Text color={textColor} fontSize="sm" fontWeight="500">
              Items per page:
            </Text>
            <Select
              value={pagination.itemsPerPage}
              onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(Number(e.target.value))}
              w="80px"
              size="sm"
              bg={inputBg}
              color={inputText}
              borderColor={borderColor}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
          </HStack>

          <HStack spacing="10px">
            <Text color={textColor} fontSize="sm" fontWeight="500">
              {pagination.total > 0 ? (
                <>
                  Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.total)} of{' '}
                  {pagination.total} entries
                </>
              ) : (
                'No entries'
              )}
            </Text>
          </HStack>

          <HStack spacing="5px">
            <Button
              size="sm"
              bg={buttonBg}
              _hover={buttonHover}
              _active={buttonHover}
              onClick={() => onPageChange && onPageChange(pagination.currentPage - 1)}
              isDisabled={pagination.currentPage === 1}
              color={textColor}
            >
              Previous
            </Button>
            <Button
              size="sm"
              bg={buttonBg}
              _hover={buttonHover}
              _active={buttonHover}
              onClick={() => onPageChange && onPageChange(pagination.currentPage + 1)}
              isDisabled={
                pagination.currentPage >= Math.ceil(pagination.total / pagination.itemsPerPage) ||
                pagination.total === 0
              }
              color={textColor}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}
    </Card>
  );
}
